/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { StorageNotificationTemplateDefault } from "@dashboard-v3/api";
import { useFetch } from "utils/api/useFetch";
import request from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import {
  TYPE_CONFIGS,
  FieldDef,
  FieldState,
  TypeConfig,
  NAME_MAX_LENGTH,
} from "../typeConfig";

const API_PATH = "/storage-notification-templates";
const DEFAULTS_PATH = "/platform/storage-notification-defaults";

export type TypeFormData = Record<string, string | boolean>;
type FormData = {
  name: string;
  [formKey: string]: string | TypeFormData | undefined;
};

type State = {
  formData: FormData;
  activeTab: number;
  saving: boolean;
  submitted: boolean;
};

export type CurrentTypeState = {
  config: TypeConfig;
  active: boolean;
  data: TypeFormData | undefined;
  savedData: TypeFormData | undefined;
  defaults: StorageNotificationTemplateDefault | null;
  loadingDefaults: boolean;
  toggle: () => void;
  setFieldState: (field: FieldDef, newState: FieldState) => void;
  setFieldValue: (fieldKey: string, value: string) => void;
  setToggleValue: (toggleKey: string, checked: boolean) => void;
};

type PartialUpdate = Partial<State> | ((prev: State) => Partial<State>);
type UpdaterFn = (data: TypeFormData) => TypeFormData;

export default function useTemplateForm() {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const [state, setState] = useState(initialState);
  const isEdit = !!id;
  const hasActiveType = TYPE_CONFIGS.some(tc => tc.formKey in state.formData);
  const currentType = TYPE_CONFIGS[state.activeTab];
  const { defaults: typeDefaults, loadingDefaults } = useTypeDefaults(
    currentType.type
  );

  const hasExistingContent =
    isEdit &&
    TYPE_CONFIGS.some(tc => {
      const data = state.formData[tc.formKey] as TypeFormData | undefined;
      return data && Object.keys(data).length > 0;
    });

  const canSubmit =
    hasActiveType &&
    (hasSubmittableContent(state.formData) || hasExistingContent);

  const {
    data: existing,
    error: loadError,
    isLoading: loadingTemplate,
  } = useFetch<Record<string, any>>(isEdit ? `${API_PATH}/${id}` : null);

  useEffect(() => {
    if (!loadError) return;
    enqueueSnackbar(t("notificationTemplates.form.loadError"), {
      variant: "error",
    });
    history.push("/integrations");
  }, [loadError]);

  useEffect(() => {
    if (!existing) return;

    const formData: FormData = { name: existing.name };

    for (const tc of TYPE_CONFIGS) {
      if (existing[tc.formKey]) {
        formData[tc.formKey] = parseTypeData(tc, existing[tc.formKey]);
      }
    }

    const firstActive = TYPE_CONFIGS.findIndex(tc => tc.formKey in formData);

    update({
      formData,
      activeTab: firstActive >= 0 ? firstActive : 0,
    });
  }, [existing]);

  function update(partial: PartialUpdate) {
    setState(prev => {
      const changes = typeof partial === "function" ? partial(prev) : partial;
      return Object.keys(changes).length === 0 ? prev : { ...prev, ...changes };
    });
  }

  function isTypeActive(formKey: string) {
    return formKey in state.formData;
  }

  function updateTypeData(updater: UpdaterFn) {
    const formKey = currentType.formKey;

    update(prev => {
      const typeData = updater({ ...getTypeData(prev.formData, formKey) });
      return { formData: { ...prev.formData, [formKey]: typeData } };
    });
  }

  function toggleType() {
    const { formKey } = currentType;

    update(prev => {
      const formData = { ...prev.formData };

      if (formKey in formData) {
        delete formData[formKey];
      } else {
        formData[formKey] = {};
      }

      return { formData };
    });
  }

  function setFieldState(field: FieldDef, newState: FieldState) {
    updateTypeData(typeData => {
      if (newState === "default") {
        delete typeData[field.key];
      } else if (newState === "custom") {
        typeData[field.key] = typeDefaults?.texts?.[field.key] ?? "";
      } else {
        typeData[field.key] = false;
      }
      return typeData;
    });
  }

  function setName(e: ChangeEvent<HTMLInputElement>) {
    const name = e.target.value;
    update(prev => ({ formData: { ...prev.formData, name } }));
  }

  function setFieldValue(fieldKey: string, value: string) {
    updateTypeData(typeData => {
      typeData[fieldKey] = value;
      return typeData;
    });
  }

  function setToggleValue(toggleKey: string, checked: boolean) {
    const defaultVal = typeDefaults?.sections?.[toggleKey] ?? true;

    updateTypeData(typeData => {
      if (checked === defaultVal) {
        delete typeData[toggleKey];
      } else {
        typeData[toggleKey] = checked;
      }

      return typeData;
    });
  }

  async function saveTemplate(formData: FormData) {
    update({ saving: true });
    try {
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `${API_PATH}/${id}` : API_PATH;
      await request(method, url, buildPayload(formData));
      const msgKey = isEdit
        ? "notificationTemplates.form.updated"
        : "notificationTemplates.form.created";
      enqueueSnackbar(t(msgKey), { variant: "success" });
      history.goBack();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("notificationTemplates.form.saveError"), {
        variant: "error",
      });
    } finally {
      update({ saving: false });
    }
  }

  async function handleSave() {
    update({ submitted: true });

    const { formData } = state;
    const invalidName =
      isBlank(formData.name) || formData.name?.length > NAME_MAX_LENGTH;
    const noContent =
      hasActiveType && !hasSubmittableContent(formData) && !hasExistingContent;
    const hasInvalidFields =
      hasEmptyCustomFields(formData) || hasFieldsExceedingMaxLength(formData);

    if (!hasActiveType) {
      enqueueSnackbar(t("notificationTemplates.form.noActiveType"), {
        variant: "error",
      });
    }

    if (noContent) {
      enqueueSnackbar(t("notificationTemplates.form.noContent"), {
        variant: "error",
      });
    }

    if (invalidName || !hasActiveType || noContent || hasInvalidFields) return;

    await saveTemplate(formData);
  }

  const isLoadingEdit = isEdit && loadingTemplate;

  const typeState: CurrentTypeState = {
    config: currentType,
    active: !isLoadingEdit && isTypeActive(currentType.formKey),
    data: state.formData[currentType.formKey] as TypeFormData | undefined,
    savedData: parseTypeData(currentType, existing?.[currentType.formKey]),
    defaults: typeDefaults,
    loadingDefaults,
    toggle: toggleType,
    setFieldState,
    setFieldValue,
    setToggleValue,
  };

  return {
    isEdit,
    name: state.formData.name,
    activeTab: state.activeTab,
    setActiveTab: (tab: number) => update({ activeTab: tab }),
    saving: state.saving,
    submitted: state.submitted,
    isLoadingEdit,
    isTypeActive,
    setName,
    handleSave,
    canSubmit,
    typeState,
  };
}

function getTypeData(formData: FormData, formKey: string): TypeFormData {
  return (formData[formKey] as TypeFormData) || {};
}

function getLayoutKeys(config: TypeConfig): Set<string> {
  return new Set(
    config.fields.filter(f => f.kind === "layout").map(f => f.key)
  );
}

function parseTypeData(tc: TypeConfig, raw: Record<string, any>) {
  if (!raw) return;

  const result: TypeFormData = {};

  for (const field of tc.fields) {
    if (field.sectionKey && raw[field.sectionKey] === false) {
      result[field.key] = false;
    } else if (typeof raw[field.key] === "string") {
      result[field.key] = raw[field.key];
    }
  }

  for (const toggle of tc.toggles || []) {
    if (typeof raw[toggle.key] === "boolean") {
      result[toggle.key] = raw[toggle.key];
    }
  }

  return result;
}

function buildPayload(formData: FormData) {
  const payload: Record<string, any> = { name: formData.name };

  for (const tc of TYPE_CONFIGS) {
    if (!(tc.formKey in formData)) continue;

    const typeData = getTypeData(formData, tc.formKey);
    const section = buildTypeSection(tc, typeData);

    if (Object.keys(section).length > 0) {
      payload[tc.formKey] = section;
    }
  }

  return payload;
}

function buildTypeSection(tc: TypeConfig, typeData: TypeFormData) {
  const section: TypeFormData = {};

  for (const [key, val] of Object.entries(typeData)) {
    if (typeof val === "string" && val !== "") {
      section[key] = val;
    }
  }

  for (const field of tc.fields) {
    if (!field.sectionKey) continue;

    const val = typeData[field.key];

    if (val === false) {
      section[field.sectionKey] = false;
    } else if (typeof val === "string" && val !== "") {
      section[field.sectionKey] = true;
    }
  }

  for (const toggle of tc.toggles || []) {
    if (typeof typeData[toggle.key] === "boolean") {
      section[toggle.key] = typeData[toggle.key];
    }
  }

  return section;
}

function hasSubmittableContent(formData: FormData) {
  return TYPE_CONFIGS.some(tc => {
    const typeData = getTypeData(formData, tc.formKey);
    if (Object.keys(typeData).length === 0) return false;

    const layoutKeys = getLayoutKeys(tc);

    return Object.entries(typeData).some(([key, val]) => {
      if (typeof val === "string") return !isBlank(val);
      if (!layoutKeys.has(key) && typeof val === "boolean") return true;
      return false;
    });
  });
}

function hasEmptyCustomFields(formData: FormData) {
  return TYPE_CONFIGS.some(tc => {
    const typeData = getTypeData(formData, tc.formKey);
    if (Object.keys(typeData).length === 0) return false;

    const layoutKeys = getLayoutKeys(tc);

    return Object.entries(typeData).some(
      ([key, val]) =>
        !layoutKeys.has(key) && typeof val === "string" && !val.trim()
    );
  });
}

function hasFieldsExceedingMaxLength(formData: FormData) {
  return TYPE_CONFIGS.some(tc => {
    const typeData = getTypeData(formData, tc.formKey);
    if (Object.keys(typeData).length === 0) return false;

    return tc.fields.some(field => {
      const val = typeData[field.key];
      return typeof val === "string" && val.length > field.maxLength;
    });
  });
}

function useTypeDefaults(type: string) {
  const path = `${DEFAULTS_PATH}?type=${type}`;
  const res = useFetch<StorageNotificationTemplateDefault>(path, {
    inmutable: true,
  });

  return { defaults: res.data || null, loadingDefaults: res.isLoading };
}

function initialState(): State {
  return {
    formData: { name: "" },
    activeTab: 0,
    saving: false,
    submitted: false,
  };
}
